<template>
  <!-- Forgot Password Modal -->
  <div
    v-if="show"
    class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
    @click.self="closeModal"
  >
    <div class="bg-white rounded-lg max-w-md w-full p-6 relative">
      <!-- Close Button -->
      <button
        @click="closeModal"
        class="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors duration-200"
      >
        <svg
          class="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M6 18L18 6M6 6l12 12"
          ></path>
        </svg>
      </button>

      <!-- Modal Header -->
      <div class="text-center mb-6">
        <div
          class="mx-auto flex items-center justify-center w-12 h-12 rounded-full bg-blue-100 mb-4"
        >
          <svg
            class="w-6 h-6 text-blue-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-3c0-.265.105-.52.293-.707l5.964-5.964A6 6 0 1121 9z"
            ></path>
          </svg>
        </div>
        <h3 class="text-lg font-semibold text-gray-900 mb-2">
          {{ currentStep === 'email' ? 'Reset Password' : 'Check Your Email' }}
        </h3>
        <p class="text-sm text-gray-600">
          {{
            currentStep === 'email'
              ? "Enter your email address and we'll send you a password reset link."
              : "We've sent a password reset link to your email address."
          }}
        </p>
      </div>

      <!-- Email Form Step -->
      <form
        v-if="currentStep === 'email'"
        @submit.prevent="handleSubmit"
        class="space-y-4"
      >
        <!-- Email Field -->
        <div>
          <label
            for="reset-email"
            class="block text-sm font-medium text-gray-700 mb-1"
          >
            Email Address
          </label>
          <div class="relative">
            <input
              id="reset-email"
              v-model="form.email"
              type="email"
              required
              autocomplete="email"
              class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
              :class="{ 'border-red-500': errors.email }"
              placeholder="Enter your email address"
              :disabled="isSubmitting"
            />
            <div class="absolute inset-y-0 right-0 pr-3 flex items-center">
              <svg
                class="w-5 h-5 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207"
                ></path>
              </svg>
            </div>
          </div>
          <p v-if="errors.email" class="mt-1 text-sm text-red-600">
            {{ errors.email }}
          </p>
        </div>

        <!-- User Type Selection -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">
            Account Type
          </label>
          <div class="grid grid-cols-2 gap-3">
            <label class="relative">
              <input
                v-model="form.userType"
                type="radio"
                value="employee"
                class="sr-only"
                :disabled="isSubmitting"
              />
              <div
                class="border-2 rounded-lg px-4 py-3 cursor-pointer transition-all duration-200"
                :class="
                  form.userType === 'employee'
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                "
              >
                <div class="flex items-center">
                  <svg
                    class="w-5 h-5 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                    ></path>
                  </svg>
                  <span class="text-sm font-medium">Employee</span>
                </div>
              </div>
            </label>
            <label class="relative">
              <input
                v-model="form.userType"
                type="radio"
                value="admin"
                class="sr-only"
                :disabled="isSubmitting"
              />
              <div
                class="border-2 rounded-lg px-4 py-3 cursor-pointer transition-all duration-200"
                :class="
                  form.userType === 'admin'
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                "
              >
                <div class="flex items-center">
                  <svg
                    class="w-5 h-5 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.031 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                    ></path>
                  </svg>
                  <span class="text-sm font-medium">Admin</span>
                </div>
              </div>
            </label>
          </div>
        </div>

        <!-- Submit Button -->
        <div class="pt-4">
          <button
            type="submit"
            :disabled="isSubmitting || !form.email || !form.userType"
            class="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
          >
            <span v-if="isSubmitting" class="inline-flex items-center">
              <svg
                class="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  class="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="4"
                ></circle>
                <path
                  class="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              Sending Reset Link...
            </span>
            <span v-else>Send Reset Link</span>
          </button>
        </div>

        <!-- Back to Login -->
        <div class="text-center pt-2">
          <button
            type="button"
            @click="closeModal"
            class="text-sm text-blue-600 hover:text-blue-500 font-medium transition-colors duration-200"
          >
            Back to Login
          </button>
        </div>
      </form>

      <!-- Success Step -->
      <div v-else-if="currentStep === 'success'" class="text-center space-y-4">
        <div
          class="mx-auto flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-4"
        >
          <svg
            class="w-8 h-8 text-green-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M5 13l4 4L19 7"
            ></path>
          </svg>
        </div>

        <div class="space-y-2">
          <p class="text-sm text-gray-600">
            We've sent a password reset link to:
          </p>
          <p class="font-medium text-gray-900">{{ form.email }}</p>
          <p class="text-sm text-gray-600">
            Click the link in your email to reset your password. The link will
            expire in 15 minutes.
          </p>
        </div>

        <!-- Resend Option -->
        <div class="pt-4">
          <button
            @click="resendEmail"
            :disabled="resendCooldown > 0"
            class="text-sm text-blue-600 hover:text-blue-500 font-medium disabled:text-gray-400 disabled:cursor-not-allowed transition-colors duration-200"
          >
            {{
              resendCooldown > 0
                ? `Resend in ${resendCooldown}s`
                : "Didn't receive the email? Resend"
            }}
          </button>
        </div>

        <!-- Action Buttons -->
        <div class="flex space-x-3 pt-4">
          <button
            @click="closeModal"
            class="flex-1 px-4 py-2 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
          >
            Close
          </button>
          <button
            @click="openEmailClient"
            class="flex-1 px-4 py-2 border border-transparent rounded-md shadow-sm bg-blue-600 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
          >
            Open Email
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, watch, onUnmounted } from 'vue';
import { authService } from '@/services/api';

export default {
  name: 'ForgotPasswordModal',
  props: {
    show: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['close', 'success'],
  setup(props, { emit }) {
    // Reactive state
    const currentStep = ref('email');
    const isSubmitting = ref(false);
    const resendCooldown = ref(0);
    const resendTimer = ref(null);

    // Form data
    const form = reactive({
      email: '',
      userType: 'employee',
    });

    // Form errors
    const errors = reactive({
      email: '',
      general: '',
    });

    // Computed properties
    const isValidEmail = computed(() => {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(form.email);
    });

    // Methods
    const validateForm = () => {
      // Clear previous errors
      Object.keys(errors).forEach(key => {
        errors[key] = '';
      });

      let isValid = true;

      // Validate email
      if (!form.email) {
        errors.email = 'Email is required';
        isValid = false;
      } else if (!isValidEmail.value) {
        errors.email = 'Please enter a valid email address';
        isValid = false;
      }

      return isValid;
    };

    const handleSubmit = async () => {
      if (!validateForm()) {
        return;
      }

      isSubmitting.value = true;
      errors.general = '';

      try {
        const endpoint =
          form.userType === 'admin'
            ? '/auth/admin/forgot-password'
            : '/auth/forgot-password';

        await authService.forgotPassword(form.email, form.userType);

        // Move to success step
        currentStep.value = 'success';
        startResendCooldown();

        // Emit success event
        emit('success', {
          email: form.email,
          userType: form.userType,
        });
      } catch (error) {
        console.error('Forgot password error:', error);

        if (error.response?.status === 404) {
          errors.email = 'No account found with this email address';
        } else if (error.response?.status === 429) {
          errors.general = 'Too many requests. Please try again later.';
        } else {
          errors.general =
            error.response?.data?.message ||
            'An error occurred. Please try again.';
        }
      } finally {
        isSubmitting.value = false;
      }
    };

    const resendEmail = async () => {
      if (resendCooldown.value > 0) return;

      try {
        await authService.forgotPassword(form.email, form.userType);
        startResendCooldown();
      } catch (error) {
        console.error('Resend email error:', error);
        errors.general = 'Failed to resend email. Please try again.';
      }
    };

    const startResendCooldown = () => {
      resendCooldown.value = 60; // 60 seconds cooldown

      resendTimer.value = setInterval(() => {
        resendCooldown.value--;
        if (resendCooldown.value <= 0) {
          clearInterval(resendTimer.value);
        }
      }, 1000);
    };

    const openEmailClient = () => {
      // Try to open default email client
      window.location.href = 'mailto:';
    };

    const closeModal = () => {
      // Reset form and state
      form.email = '';
      form.userType = 'employee';
      currentStep.value = 'email';
      isSubmitting.value = false;
      resendCooldown.value = 0;

      // Clear errors
      Object.keys(errors).forEach(key => {
        errors[key] = '';
      });

      // Clear timer
      if (resendTimer.value) {
        clearInterval(resendTimer.value);
        resendTimer.value = null;
      }

      emit('close');
    };

    // Watch for modal close
    watch(
      () => props.show,
      newValue => {
        if (!newValue) {
          closeModal();
        }
      }
    );

    // Auto-clear email error when user types
    watch(
      () => form.email,
      () => {
        if (errors.email) {
          errors.email = '';
        }
      }
    );

    // Cleanup on unmount
    onUnmounted(() => {
      if (resendTimer.value) {
        clearInterval(resendTimer.value);
      }
    });

    return {
      // Reactive state
      currentStep,
      isSubmitting,
      resendCooldown,
      form,
      errors,

      // Computed
      isValidEmail,

      // Methods
      handleSubmit,
      resendEmail,
      openEmailClient,
      closeModal,
    };
  },
};
</script>

<style scoped>
/* Modal animations */
.fixed.inset-0 {
  animation: fadeIn 0.2s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.fixed.inset-0 > div {
  animation: slideUp 0.3s ease-out;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Radio button custom styling */
input[type='radio']:checked + div {
  transform: scale(1.02);
}

/* Focus states */
input:focus {
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

button:focus {
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

/* Smooth transitions */
* {
  transition-property: color, background-color, border-color,
    text-decoration-color, fill, stroke, opacity, box-shadow, transform;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 150ms;
}
</style>
